<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12" class="pr-6">
      <card-title-row :straightTitle="additionalModuleName" class="pt-3 pb-5" />
      <sidebar-form
        :is-valid.sync="isFormValid"
        :inputs="inputs"
        v-model="params"
        :is-editing-on="!isStickyButtonsLoading"
        hide-rules-icon
      >
        <template #mask_layer="scope">
          <v-tooltip bottom max-width="300" :disabled="!params[scope.name]">
            <template #activator="{ on }">
              <span v-on="on">
                <data-input v-model="params[scope.name]" v-bind="scope" />
              </span>
            </template>
            {{ params[scope.name] ? layers[params[scope.name]].name : '' }}
          </v-tooltip>
        </template>
        <template #mask_features="scope">
          <base-button-subheader :subheader-translation-path="scope.subtitlePath">
            <dots-menu
              :items="scope.dotsItems"
              @single="turnOnSelecting('single')"
              @multiple="turnOnSelecting('multiple')"
              @freehand="turnOnSelecting('freehand')"
            >
              <template #activator="{ on }">
                <v-badge :content="selectedFeaturesLength" :value="isAnyFeatureSelected" color="error" overlap>
                  <base-button
                    v-on="getButtonListeners(on)"
                    @click="turnOffSelecting"
                    class="mr-0"
                    :translation-path="scope.translationPath"
                    :button-style="scope.buttonStyle"
                    :disabled="!currentSelectedLayer"
                  >
                  </base-button>
                </v-badge>
              </template>
            </dots-menu>
          </base-button-subheader>
          <template v-if="isAnyFeatureSelected">
            <v-row no-gutters class="justify-end">
              <v-col cols="auto">
                <clickable-text @click="deleteFeature(null)">
                  {{ $i18n.t('sidebar.sewerProfile.deleteAllLinks') }}
                </clickable-text>
              </v-col>
            </v-row>
            <ribbon-list
              :listItems="computedCurrentSelectedFeatures"
              styleType="raw"
              @mouseleave="onElementHover($event, false)"
              @mouseenter="onElementHover($event, true)"
            >
              <template #itemButtons-prepend="{ item }">
                <v-icon @click="deleteFeature(item.value)" color="rgba(0,0,0,.7)" size="19px">mdi-close</v-icon>
              </template>
            </ribbon-list>
          </template>
        </template>
      </sidebar-form>
      <sidebar-row
        class="pt-3"
        padding-right="pr-0"
        v-if="infrastructureCollisionsResult && infrastructureCollisionsResult.length"
      >
        <ribbon-list
          :listItems="infrastructureCollisionsResult"
          styleType="raw"
          titlePath="sidebar.infrastructureCollisions.results"
          @mouseleave="onResultHover($event, false)"
          @mouseenter="onResultHover($event, true)"
        >
          <template #itemButtons-prepend="{ item }">
            <v-icon @click="zoomToFeature(item.geometry)" color="rgba(0,0,0,.7)" size="19px">mdi-magnify</v-icon>
          </template>
        </ribbon-list>
      </sidebar-row>
    </v-col>
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';
import identificationSidebarCard from '@/mixins/identificationSidebarCard';

import CardTitleRow from '@/components/CardTitleRow';
import ClickableText from '@/components/ClickableText';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import RibbonList from '@/components/RibbonList';
import SidebarForm from '@/components/SidebarForm';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'InfrastructureCollisions',
  components: { CardTitleRow, ClickableText, DataInput, DotsMenu, RibbonList, SidebarForm, SidebarRow },
  mixins: [identificationSidebarCard],
  computed: {
    activeTool: get('tools/activeTool'),
    additionalModuleName: get('admin/additionalModules@INFRASTRUCTURE_COLLISIONS.verbose_name'),
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    infrastructureCollisionsLayers: get('admin/settingsValues@infrastructure_collisions_layers'),
    inputs() {
      return [
        {
          name: 'mask_layer',
          dataType: { name: 'select' },
          items: this.projectMappedLayers,
          translationPath: 'sidebar.infrastructureCollisions.inputLayer',
          cols: '7',
          nameCols: '5',
          clearable: false,
          rules: [v => !!v || v === 0 || 'rules.required'],
          hideDetails: true,
          alignClass: 'start',
        },
        {
          name: 'mask_features',
          translationPath: 'sidebar.infrastructureCollisions.selectFeatures',
          subtitlePath: 'sidebar.infrastructureCollisions.selectFeaturesSubtitle',
          cols: '12',
          hasNameColumn: false,
          buttonStyle: this.isButtonActive
            ? {
                backgroundColor: `${this.$_colors.primarybackground} !important`,
                border: '1px solid rgba(26, 115, 232, 0.3) !important',
                color: this.$_colors.primary,
              }
            : {
                backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                border: '1px solid rgba(0, 0, 0, 0.2) !important',
                color: '#61646D',
              },
          dotsItems: [
            {
              name: 'single',
            },
            {
              name: 'multiple',
            },
            {
              name: 'freehand',
            },
          ],
        },
        {
          name: 'tolerance',
          dataType: { name: 'float' },
          allowNegative: false,
          translationPath: 'sidebar.infrastructureCollisions.buffer',
          hasSubtitle: true,
          subtitlePath: 'sidebar.infrastructureCollisions.bufferSubtitle',
          cols: '7',
          nameCols: '5',
          hasDivider: this.infrastructureCollisionsResult?.length ? true : false,
        },
      ];
    },

    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    projectMappedLayers() {
      return this.projectLayers
        .filter(layer => layer.type === 'features_layer')
        .map(layer => {
          return { text: layer.name, value: layer.id };
        });
    },
    currentSelectedLayerId() {
      return this.params.mask_layer || null;
    },
    currentSelectedLayer() {
      return this.layers[this.currentSelectedLayerId] || undefined;
    },
    currentSelectedDataSourceName() {
      return this.currentSelectedLayer?.data_source_name || '';
    },
    currentSelectedDataSource() {
      return this.dataSources[this.currentSelectedDataSourceName] || undefined;
    },
    currentSelectedDataSourceGeomAttrName() {
      return this.currentSelectedDataSource?.attributes_schema.geometry_name || '';
    },

    currentSelectedFeatures: get('map/selectedFeatures@:currentSelectedLayerId'),
    computedCurrentSelectedFeatures() {
      return (this.currentSelectedFeatures || []).map(feature => {
        return { value: feature, title: `ID: ${feature}` };
      });
    },
    selectedFeaturesLength() {
      return this.computedCurrentSelectedFeatures?.length || 0;
    },
    isAnyFeatureSelected() {
      return this.selectedFeaturesLength > 0;
    },

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
  },
  data: () => ({
    infrastructureCollisionsResult: [],
    isButtonActive: false,
    isFormValid: false,
    params: {
      mask_layer: null,
      tolerance: null,
    },
  }),
  methods: {
    getInfrastructureCollisions: call('layers/getInfrastructureCollisions'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    async checkCollisions() {
      try {
        this.isStickyButtonsLoading = true;
        const params = {
          ...JSON.parse(JSON.stringify({ ...this.params, tolerance: this.params.tolerance || 0 })),
          mask_features: this.currentSelectedFeatures,
          layers: this.infrastructureCollisionsLayers,
        };
        const data = (await this.getInfrastructureCollisions(params))?.data?.data || {};
        let infrastructureCollisionsResult = [];
        Object.keys(data).forEach(layerId => {
          const layer = this.layers[layerId];
          if (layer) {
            infrastructureCollisionsResult = [
              ...infrastructureCollisionsResult,
              ...data[layerId].map(feature => {
                return { ...feature, title: `${layer.name}: ${feature.id}` };
              }),
            ];
          }
        });
        this.infrastructureCollisionsResult = infrastructureCollisionsResult;
        this.infrastructureCollisionsResult.sort((a, b) => {
          return a.title.localeCompare(b.title);
        });
        this.stickyButtons = [
          ...this.stickyButtons.map(button => {
            return { ...button, disabled: true };
          }),
          {
            action: 'resetInfrastructureCollisions',
            translationPath: 'dialog.reset',
            cancel: true,
            preventGoToLegendCancel: true,
          },
        ];
        this.isStickyButtonsLoading = false;
      } catch {
        this.isStickyButtonsLoading = false;
      }
    },
    clearSelection(layerId) {
      this.turnOffSelecting();
      if (layerId || layerId === 0) {
        this.deleteFeature(null, layerId);
      } else {
        this.deleteFeature(null);
      }
    },
    deleteFeature(id, layerId = this.currentSelectedLayerId) {
      const deletedIds = id ? [id] : [];
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: deletedIds,
        },
      });
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'highlitedFeatures',
        features: {
          [layerId]: deletedIds,
        },
      });
    },
    getButtonListeners(on) {
      return this.isButtonActive
        ? {
            click: this.turnOffSelecting,
          }
        : on;
    },
    onElementHover(item, value) {
      this.onHover({ id: item.value, layerId: this.currentSelectedLayerId }, value);
    },
    onResultHover(feature, value) {
      this.$root.$emit('toggleFeatureHover', feature, value);
    },
    zoomToFeature(geometry) {
      this.$root.$emit('createGeomAndFitView', geometry);
    },
    resetInfrastructureCollisions() {
      this.infrastructureCollisionsResult = {};
      this.clearSelection();
      this.stickyButtons = [
        ...[this.stickyButtons[0]].map(button => {
          return { ...button, disabled: false };
        }),
      ];
    },
    turnOffSelecting() {
      if (this.activeTool !== 'selection') {
        return;
      }
      this.$root.$emit('selection-action');
      this.isButtonActive = false;
    },
    turnOnSelecting(type) {
      if (type === 'single') {
        this.$root.$emit('attachOnClickSelection', this.currentSelectedLayerId, false);
      } else if (type === 'multiple') {
        this.$root.$emit('selection-action', 'multiple', {
          selectionBoxAction: this.getIntersectingFeatures,
          layerId: this.currentSelectedLayerId,
        });
      } else {
        this.$root.$emit('selection-action', 'freehand', {
          selectionBoxAction: this.getIntersectingFeatures,
          layerId: this.currentSelectedLayerId,
        });
      }
      this.isButtonActive = true;
    },
    async getIntersectingFeatures(geometry) {
      const r = await this.getLayerFeatures({
        features_filter: {
          '%SPATIALLY_INTERSECTS': {
            [`${this.currentSelectedDataSourceName}.${this.currentSelectedDataSourceGeomAttrName}`]: [geometry],
          },
        },
        layer_id: this.currentSelectedLayerId,
        params: {
          ids_only: true,
        },
      });
      return r;
    },
  },
  watch: {
    activeTool(nV, oV) {
      if (nV !== oV && oV === 'selection') {
        this.isButtonActive = false;
      }
    },
    currentSelectedLayerId(nV, oV) {
      this.clearSelection(oV);
    },
    projectLayers: {
      deep: true,
      immediate: true,
      handler(nV) {
        if (nV.find(layer => layer.id === this.currentSelectedLayerId)) {
          return;
        } else {
          this.params.mask_layer = nV[0]?.id || null;
        }
      },
    },
  },
  mounted() {
    this.$root.$off('checkInfrastructureCollisions');
    this.$root.$on('checkInfrastructureCollisions', this.checkCollisions);
    this.$root.$off('resetInfrastructureCollisions');
    this.$root.$on('resetInfrastructureCollisions', this.resetInfrastructureCollisions);
    this.stickyButtons = [
      {
        action: 'checkInfrastructureCollisions',
        translationPath: 'dialog.agreeCheck',
      },
    ];
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsValid = false;
    this.isStickyButtonsVisible = true;
    this.$watch(
      vm => {
        return { isFormValid: vm.isFormValid, isAnyFeatureSelected: vm.isAnyFeatureSelected };
      },
      nV => {
        const { isFormValid, isAnyFeatureSelected } = nV;
        this.isStickyButtonsValid = isFormValid && isAnyFeatureSelected;
      }
    );
  },
  beforeDestroy() {
    this.clearSelection();
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsVisible = false;
  },
};
</script>
